import { useReactiveVar } from "@apollo/client"
import { useMemo } from "react"

import { useUser } from "~contexts/user-provider"
import { useOutfitterContext } from "~contexts/outfitter-provider"
import {
    getEditEmailTemplateIdFromPathname,
    getEditFormIdFromPathname,
    ROUTES,
} from "~utils/helpers/navigation-helpers"
import { OUTFITTER_ASSISTANT_VISUAL_CONTEXT_MODE_NONE } from "~components/outfitter-assistant/outfitter-assistant-constants"
import { outfitterAssistantPathnameVar } from "~utils/outfitter-assistant-pathname-var"
import { outfitterAssistantHistoryVar } from "~utils/outfitter-assistant-history-var"

const UNKNOWN_SCREEN_NAME = "UNKNOWN"

type OutfitterAssistantRouteKey = keyof typeof ROUTES
export type OutfitterAssistantScreenName =
    | OutfitterAssistantRouteKey
    | typeof UNKNOWN_SCREEN_NAME

export default function useOutfitterAssistantContext(): Record<
    string,
    unknown
> {
    const pathname = useReactiveVar(outfitterAssistantPathnameVar)
    const history = useReactiveVar(outfitterAssistantHistoryVar)
    const { user } = useUser()
    const { outfitterId } = useOutfitterContext()

    return useMemo(() => {
        const screenName = pathnameToScreenName(pathname)
        const userId = user?.id ?? ""
        const messageTemplateId = getEditEmailTemplateIdFromPathname(pathname)
        const formId = getEditFormIdFromPathname(pathname)
        const entityIds =
            outfitterId || messageTemplateId || formId
                ? {
                      ...(outfitterId ? { outfitterId } : {}),
                      ...(messageTemplateId ? { messageTemplateId } : {}),
                      ...(formId ? { formId } : {}),
                  }
                : undefined

        return {
            session: {
                userId,
                ...(outfitterId ? { outfitterId } : {}),
            },
            page: {
                screenName,
                pathname,
                ...(entityIds ? { entityIds } : {}),
                ...(history.length > 0 ? { history } : {}),
            },
            visualContext: {
                mode: OUTFITTER_ASSISTANT_VISUAL_CONTEXT_MODE_NONE,
            },
        }
    }, [pathname, user?.id, outfitterId, history])
}

export function pathnameToScreenName(
    pathname: string
): OutfitterAssistantScreenName {
    const normalized = normalizePathname(pathname)

    const entries = Object.entries(ROUTES)
        .map(([key, path]) => ({
            key,
            base: routeBaseForMatching(path),
        }))
        .sort((left, right) => right.base.length - left.base.length)

    const matched = entries.find(({ base }) => {
        if (base === "/") {
            return normalized === "/" || normalized === ""
        }
        return normalized === base || normalized.startsWith(`${base}/`)
    })

    return isOutfitterAssistantScreenName(matched?.key)
        ? matched.key
        : UNKNOWN_SCREEN_NAME
}

export function isOutfitterAssistantScreenName(
    value: unknown
): value is OutfitterAssistantScreenName {
    if (value === UNKNOWN_SCREEN_NAME) return true
    return typeof value === "string" && value in ROUTES
}

function normalizePathname(pathname: string): string {
    if (pathname.length > 1 && pathname.endsWith("/")) {
        return pathname.slice(0, -1)
    }
    return pathname
}

function routeBaseForMatching(routePath: string): string {
    const withoutParams = routePath.replace(/\/:\w+(?=\/|$)/g, "")
    const trimmed = withoutParams.replace(/\/$/, "")
    return trimmed === "" ? "/" : trimmed
}
